/**
 * Lease Service
 * Handles lease creation, templates, and e-signing workflow
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { getFirebaseDB, getFirebaseAuth } from "@/lib/firebase";
import { LeaseAgreement } from "@/types/types";
import { notifyLeaseCreated, notifyLeaseSigned } from "./notification-service";
import {
  updateProperty,
  getPropertyById,
  PaginatedResult,
} from "./property-service";
import { getApplicationById } from "./application-service";

/**
 * Create a lease from an approved application
 */
export async function createLeaseFromApplication(
  applicationId: string,
  landlordId: string,
  customTerms?: Partial<LeaseAgreement>
): Promise<string> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  // Get application details
  const application = await getApplicationById(applicationId);
  if (!application) {
    throw new Error("Application not found");
  }

  if (application.status !== "approved") {
    throw new Error("Application must be approved before creating lease");
  }

  // Get property details for rent amount
  const property = await getPropertyById(application.propertyId);
  if (!property) {
    throw new Error("Property not found");
  }

  const leasesRef = collection(db, "leases");
  const newLeaseRef = doc(leasesRef);

  const lease: Omit<LeaseAgreement, "id"> = {
    propertyId: application.propertyId,
    landlordId,
    tenantId: application.tenantId,
    applicationId,

    // Lease terms
    startDate: customTerms?.startDate || application.moveInDate,
    endDate:
      customTerms?.endDate || calculateEndDate(application.moveInDate, 12),
    monthlyRent: customTerms?.monthlyRent || property.rent,
    securityDeposit: customTerms?.securityDeposit || property.rent * 1, // 1 month default

    // Payment terms
    paymentDueDay: customTerms?.paymentDueDay || 1,
    lateFeeAmount: customTerms?.lateFeeAmount || property.rent * 0.05, // 5% default
    lateFeeGracePeriod: customTerms?.lateFeeGracePeriod || 5,

    // Utilities and responsibilities
    utilitiesIncluded: customTerms?.utilitiesIncluded || [],
    tenantResponsibilities: customTerms?.tenantResponsibilities || [
      "Maintain cleanliness of the property",
      "Report maintenance issues promptly",
      "Pay rent on time",
      "No unauthorized modifications",
    ],
    landlordResponsibilities: customTerms?.landlordResponsibilities || [
      "Maintain property in habitable condition",
      "Handle major repairs",
      "Provide 24-hour notice before entry",
    ],

    // Rules and restrictions
    petsAllowed: customTerms?.petsAllowed || false,
    smokingAllowed: customTerms?.smokingAllowed || false,
    sublettingAllowed: customTerms?.sublettingAllowed || false,
    maxOccupants: customTerms?.maxOccupants || 2,

    // Legal
    terminationNotice: customTerms?.terminationNotice || 60, // days
    renewalTerms:
      customTerms?.renewalTerms ||
      "Lease may be renewed upon mutual agreement 60 days before expiration",

    // Status
    status: "pending_signatures",

    // Signatures
    landlordSigned: false,
    tenantSigned: false,

    // Timestamps
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  await setDoc(newLeaseRef, {
    ...lease,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Notify tenant
  await notifyLeaseCreated(
    application.tenantId,
    newLeaseRef.id,
    application.propertyTitle
  );

  return newLeaseRef.id;
}

/**
 * Sign a lease (landlord or tenant)
 */
export async function signLease(
  leaseId: string,
  userId: string,
  signatureData: string,
  ipAddress?: string
): Promise<void> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const leaseRef = doc(db, "leases", leaseId);
  const leaseSnap = await getDoc(leaseRef);

  if (!leaseSnap.exists()) {
    throw new Error("Lease not found");
  }

  const lease = leaseSnap.data() as LeaseAgreement;

  // Debug logging
  console.log("Lease data from Firestore:", {
    leaseId,
    tenantId: lease.tenantId,
    landlordId: lease.landlordId,
    userId,
    hastenantId: !!lease.tenantId,
    hasLandlordId: !!lease.landlordId,
  });

  // Determine if this is landlord or tenant signing
  const isLandlord = lease.landlordId === userId;
  const isTenant = lease.tenantId === userId;

  if (!isLandlord && !isTenant) {
    throw new Error(
      `User is not authorized to sign this lease. User ID: ${userId}, Tenant ID: ${
        lease.tenantId || "MISSING"
      }, Landlord ID: ${lease.landlordId || "MISSING"}`
    );
  }

  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (isLandlord) {
    if (lease.landlordSigned) {
      throw new Error("Landlord has already signed this lease");
    }
    updateData.landlordSigned = true;
    updateData.landlordSignedAt = serverTimestamp();
    updateData.landlordSignature = signatureData;
    updateData.landlordSignatureIp = ipAddress || "";
  } else {
    if (lease.tenantSigned) {
      throw new Error("Tenant has already signed this lease");
    }
    updateData.tenantSigned = true;
    updateData.tenantSignedAt = serverTimestamp();
    updateData.tenantSignature = signatureData;
    updateData.tenantSignatureIp = ipAddress || "";
  }

  // Update the lease document first
  await updateDoc(leaseRef, updateData);

  // Check if lease is now fully signed
  const bothSigned =
    (isLandlord && lease.tenantSigned) || (isTenant && lease.landlordSigned);

  if (bothSigned) {
    // Update lease status to active
    await updateDoc(leaseRef, {
      status: "active",
      activatedAt: serverTimestamp(),
    });

    // Update property status to occupied (only landlord has permission)
    // This will be done by a Cloud Function or landlord separately
    try {
      await updateProperty(lease.propertyId, {
        status: "occupied",
      });
    } catch (error) {
      console.warn(
        "Could not update property status (may require landlord permissions):",
        error
      );
      // Non-critical error, lease is still signed
    }

    // Notify both parties
    try {
      await notifyLeaseSigned(lease.landlordId, leaseId);
      await notifyLeaseSigned(lease.tenantId, leaseId);
    } catch (error) {
      console.warn("Could not send notifications:", error);
    }
  } else {
    // Notify the other party that one signature is complete
    try {
      const otherPartyId = isLandlord ? lease.tenantId : lease.landlordId;
      await notifyLeaseCreated(otherPartyId, leaseId, "Lease signature needed");
    } catch (error) {
      console.warn("Could not send notification:", error);
    }
  }
}

/**
 * Get lease by ID
 */
export async function getLeaseById(
  leaseId: string
): Promise<LeaseAgreement | null> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const leaseRef = doc(db, "leases", leaseId);
  const leaseSnap = await getDoc(leaseRef);

  if (!leaseSnap.exists()) {
    return null;
  }

  const data = leaseSnap.data();

  // Fetch property details
  let propertyTitle = "Unknown Property";
  try {
    const propertyDoc = await getDoc(doc(db, "properties", data.propertyId));
    if (propertyDoc.exists()) {
      propertyTitle = propertyDoc.data().title || propertyTitle;
    }
  } catch (error) {
    console.error("Error fetching property:", error);
  }

  // Fetch tenant details
  let tenantName = "Unknown Tenant";
  try {
    const tenantDoc = await getDoc(doc(db, "users", data.tenantId));
    if (tenantDoc.exists()) {
      tenantName =
        tenantDoc.data().name || tenantDoc.data().displayName || tenantName;
    }
  } catch (error) {
    console.error("Error fetching tenant:", error);
  }

  return {
    ...data,
    id: leaseSnap.id,
    propertyTitle, // Add property title
    tenantName, // Add tenant name
    createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
    startDate: data.startDate?.toDate?.()?.toISOString() || data.startDate,
    endDate: data.endDate?.toDate?.()?.toISOString() || data.endDate,
    landlordSignedAt:
      data.landlordSignedAt?.toDate?.()?.toISOString() || data.landlordSignedAt,
    tenantSignedAt:
      data.tenantSignedAt?.toDate?.()?.toISOString() || data.tenantSignedAt,
    activatedAt:
      data.activatedAt?.toDate?.()?.toISOString() || data.activatedAt,
  } as LeaseAgreement;
}

/**
 * Get leases for a landlord
 */
export async function getLandlordLeases(
  landlordId: string,
  pageSize: number = 20,
  lastDocument?: QueryDocumentSnapshot<DocumentData>
): Promise<PaginatedResult<LeaseAgreement>> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const leasesRef = collection(db, "leases");
  let q = query(
    leasesRef,
    where("landlordId", "==", landlordId),
    orderBy("createdAt", "desc"),
    limit(pageSize + 1)
  );

  if (lastDocument) {
    q = query(q, startAfter(lastDocument));
  }

  const querySnapshot = await getDocs(q);

  const hasMore = querySnapshot.size > pageSize;
  const docs = hasMore
    ? querySnapshot.docs.slice(0, pageSize)
    : querySnapshot.docs;

  // Fetch property and tenant details for each lease
  const leasePromises = docs.map(async (leaseDoc) => {
    const data = leaseDoc.data();

    // Fetch property details
    let propertyTitle = "Unknown Property";
    try {
      const propertyDoc = await getDoc(doc(db, "properties", data.propertyId));
      if (propertyDoc.exists()) {
        propertyTitle = propertyDoc.data().title || propertyTitle;
      }
    } catch (error) {
      console.error("Error fetching property:", error);
    }

    // Fetch tenant details
    let tenantName = "Unknown Tenant";
    try {
      const tenantDoc = await getDoc(doc(db, "users", data.tenantId));
      if (tenantDoc.exists()) {
        tenantName =
          tenantDoc.data().name || tenantDoc.data().displayName || tenantName;
      }
    } catch (error) {
      console.error("Error fetching tenant:", error);
    }

    return {
      ...data,
      id: leaseDoc.id,
      propertyTitle, // Add property title
      tenantName, // Add tenant name
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      startDate: data.startDate?.toDate?.()?.toISOString() || data.startDate,
      endDate: data.endDate?.toDate?.()?.toISOString() || data.endDate,
      landlordSignedAt:
        data.landlordSignedAt?.toDate?.()?.toISOString() ||
        data.landlordSignedAt,
      tenantSignedAt:
        data.tenantSignedAt?.toDate?.()?.toISOString() || data.tenantSignedAt,
      activatedAt:
        data.activatedAt?.toDate?.()?.toISOString() || data.activatedAt,
    } as LeaseAgreement;
  });

  const resolvedLeases = await Promise.all(leasePromises);

  return {
    data: resolvedLeases,
    lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore,
  };
}

/**
 * Get all landlord leases (backward compatible - no pagination)
 * @deprecated Use getLandlordLeases with pagination instead
 */
export async function getAllLandlordLeases(
  landlordId: string
): Promise<LeaseAgreement[]> {
  const result = await getLandlordLeases(landlordId, 1000);
  return result.data;
}

/**
 * Get leases for a tenant
 */
export async function getTenantLeases(
  tenantId: string,
  pageSize: number = 20,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null
): Promise<PaginatedResult<LeaseAgreement>> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const leasesRef = collection(db, "leases");

  // Fetch one more than pageSize to check if there are more results
  let q = query(
    leasesRef,
    where("tenantId", "==", tenantId),
    orderBy("createdAt", "desc"),
    limit(pageSize + 1)
  );

  if (lastDocument) {
    q = query(
      leasesRef,
      where("tenantId", "==", tenantId),
      orderBy("createdAt", "desc"),
      startAfter(lastDocument),
      limit(pageSize + 1)
    );
  }

  const querySnapshot = await getDocs(q);
  const docs = querySnapshot.docs;
  const hasMore = docs.length > pageSize;
  const paginatedDocs = hasMore ? docs.slice(0, pageSize) : docs;

  const leases: LeaseAgreement[] = [];

  paginatedDocs.forEach((doc) => {
    const data = doc.data();
    leases.push({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      startDate: data.startDate?.toDate?.()?.toISOString() || data.startDate,
      endDate: data.endDate?.toDate?.()?.toISOString() || data.endDate,
      landlordSignedAt:
        data.landlordSignedAt?.toDate?.()?.toISOString() ||
        data.landlordSignedAt,
      tenantSignedAt:
        data.tenantSignedAt?.toDate?.()?.toISOString() || data.tenantSignedAt,
      activatedAt:
        data.activatedAt?.toDate?.()?.toISOString() || data.activatedAt,
    } as LeaseAgreement);
  });

  return {
    data: leases,
    lastDoc:
      paginatedDocs.length > 0 ? paginatedDocs[paginatedDocs.length - 1] : null,
    hasMore,
  };
}

/**
 * Get all tenant leases without pagination (backward compatibility)
 */
export async function getAllTenantLeases(
  tenantId: string
): Promise<LeaseAgreement[]> {
  const result = await getTenantLeases(tenantId, 1000);
  return result.data;
}

/**
 * Terminate a lease
 */
export async function terminateLease(
  leaseId: string,
  terminatedBy: string,
  reason: string
): Promise<void> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const leaseRef = doc(db, "leases", leaseId);
  const leaseSnap = await getDoc(leaseRef);

  if (!leaseSnap.exists()) {
    throw new Error("Lease not found");
  }

  const lease = leaseSnap.data() as LeaseAgreement;

  await updateDoc(leaseRef, {
    status: "terminated",
    terminatedAt: serverTimestamp(),
    terminatedBy,
    terminationReason: reason,
    updatedAt: serverTimestamp(),
  });

  // Update property status
  await updateProperty(lease.propertyId, {
    status: "vacant",
  });
}

/**
 * Generate lease PDF (placeholder - would use a PDF library)
 */
export async function generateLeasePDF(leaseId: string): Promise<string> {
  // This would integrate with a PDF generation service
  // For now, return a placeholder URL
  return `/api/leases/${leaseId}/pdf`;
}

/**
 * Helper: Calculate end date from start date and duration
 */
function calculateEndDate(startDate: string, months: number): string {
  const date = new Date(startDate);
  date.setMonth(date.getMonth() + months);
  return date.toISOString();
}

/**
 * Check if lease is expiring soon
 */
export async function getExpiringLeases(
  daysBeforeExpiry: number = 60,
  pageSize: number = 20,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null
): Promise<PaginatedResult<LeaseAgreement>> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const leasesRef = collection(db, "leases");

  // Fetch more than pageSize since we filter client-side by date
  let q = query(
    leasesRef,
    where("status", "==", "active"),
    orderBy("endDate", "asc"),
    limit(pageSize * 2) // Fetch more to account for date filtering
  );

  if (lastDocument) {
    q = query(
      leasesRef,
      where("status", "==", "active"),
      orderBy("endDate", "asc"),
      startAfter(lastDocument),
      limit(pageSize * 2)
    );
  }

  const querySnapshot = await getDocs(q);
  const docs = querySnapshot.docs;
  const expiringLeases: LeaseAgreement[] = [];

  const expiryThreshold = new Date();
  expiryThreshold.setDate(expiryThreshold.getDate() + daysBeforeExpiry);

  let lastValidDoc: QueryDocumentSnapshot<DocumentData> | null = null;

  for (const doc of docs) {
    if (expiringLeases.length >= pageSize) break;

    const data = doc.data();
    const endDate = new Date(data.endDate?.toDate?.() || data.endDate);

    if (endDate <= expiryThreshold && endDate >= new Date()) {
      expiringLeases.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
        startDate: data.startDate?.toDate?.()?.toISOString() || data.startDate,
        endDate: data.endDate?.toDate?.()?.toISOString() || data.endDate,
        landlordSignedAt:
          data.landlordSignedAt?.toDate?.()?.toISOString() ||
          data.landlordSignedAt,
        tenantSignedAt:
          data.tenantSignedAt?.toDate?.()?.toISOString() || data.tenantSignedAt,
        activatedAt:
          data.activatedAt?.toDate?.()?.toISOString() || data.activatedAt,
      } as LeaseAgreement);
      lastValidDoc = doc;
    }
  }

  return {
    data: expiringLeases,
    lastDoc: lastValidDoc,
    hasMore: docs.length >= pageSize * 2 && expiringLeases.length === pageSize,
  };
}

/**
 * Get all expiring leases without pagination (backward compatibility)
 */
export async function getAllExpiringLeases(
  daysBeforeExpiry: number = 60
): Promise<LeaseAgreement[]> {
  const result = await getExpiringLeases(daysBeforeExpiry, 1000);
  return result.data;
}

/**
 * Create a direct lease without going through application process
 * This is used when landlords manually add tenants
 */
export interface CreateDirectLeaseData {
  tenantEmail: string;
  tenantName: string;
  tenantPhone: string;
  propertyId: string;
  unitId?: string; // For multi-unit properties
  monthlyRent: number;
  securityDeposit: number;
  startDate: string;
  endDate: string;
  occupation?: string;
  employer?: string;
  emergencyContact?: string;
  notes?: string;
}

export async function createDirectLease(
  data: CreateDirectLeaseData,
  landlordId: string
): Promise<string> {
  const db = getFirebaseDB();
  const auth = getFirebaseAuth();
  if (!db) throw new Error("Firebase not initialized");
  if (!auth) throw new Error("Firebase Auth not initialized");

  // Get property details
  const property = await getPropertyById(data.propertyId);
  if (!property) {
    throw new Error("Property not found");
  }

  // Check if this is a multi-unit property and unit is required
  if (property.structure === "multi-unit" && !data.unitId) {
    throw new Error("Unit selection is required for multi-unit properties");
  }

  // Check if unit exists and is available (for multi-unit)
  if (data.unitId && property.units) {
    const unit = property.units.find((u) => u.id === data.unitId);
    if (!unit) {
      throw new Error("Selected unit not found");
    }
    if (unit.status === "occupied") {
      throw new Error("Selected unit is already occupied");
    }
  }

  // Check if tenant user exists in Firebase Auth
  let tenantId: string;
  let tenantExists = false;
  let leaseStatus: "pending_signatures" | "pending_tenant_signup" =
    "pending_tenant_signup";

  const usersRef = collection(db, "users");
  const userQuery = query(usersRef, where("email", "==", data.tenantEmail));
  const userSnap = await getDocs(userQuery);

  if (!userSnap.empty) {
    // Check for duplicate emails (should never happen, but prevent issues)
    if (userSnap.size > 1) {
      console.error("DUPLICATE EMAILS FOUND:", {
        email: data.tenantEmail,
        count: userSnap.size,
        userIds: userSnap.docs.map((d) => d.id),
      });
      throw new Error(
        "Multiple users found with this email. Please contact support to resolve this issue."
      );
    }

    // Tenant exists - use their Firebase Auth UID
    tenantId = userSnap.docs[0].id;
    tenantExists = true;
    leaseStatus = "pending_signatures";

    // Check if tenant already has an active lease for this property
    const existingLeasesRef = collection(db, "leases");
    const existingLeaseQuery = query(
      existingLeasesRef,
      where("tenantId", "==", tenantId),
      where("propertyId", "==", data.propertyId),
      where("status", "in", [
        "active",
        "pending_invitation",
        "pending_signatures",
        "pending_tenant_signup",
      ])
    );
    const existingLeases = await getDocs(existingLeaseQuery);

    if (!existingLeases.empty) {
      throw new Error("Tenant already has an active lease for this property");
    }
  } else {
    // Tenant doesn't exist yet - create lease with email placeholder
    // Will be linked when tenant signs up
    tenantId = `pending_${data.tenantEmail}`; // Temporary ID
    tenantExists = false;
    leaseStatus = "pending_tenant_signup";
  }

  // Create lease document
  const leasesRef = collection(db, "leases");
  const newLeaseRef = doc(leasesRef);

  const lease: Partial<LeaseAgreement> & { tenantEmail?: string } = {
    propertyId: data.propertyId,
    ...(data.unitId && { unitId: data.unitId }), // Only include if defined
    landlordId,
    tenantId,
    // Store email for pending tenants so we can link them when they sign up
    ...(leaseStatus === "pending_tenant_signup" && {
      tenantEmail: data.tenantEmail,
    }),
    // applicationId is omitted for direct leases (no undefined values)

    // Lease terms
    startDate: data.startDate,
    endDate: data.endDate,
    monthlyRent: data.monthlyRent,
    securityDeposit: data.securityDeposit,

    // Payment terms with defaults
    paymentDueDay: 1,
    lateFeeAmount: data.monthlyRent * 0.05, // 5% late fee
    lateFeeGracePeriod: 5, // 5 days grace period

    // Utilities and responsibilities (defaults)
    utilitiesIncluded: [],
    tenantResponsibilities: [
      "Maintain cleanliness of the property",
      "Report maintenance issues promptly",
      "Pay rent on time",
      "No unauthorized modifications",
    ],
    landlordResponsibilities: [
      "Maintain property in habitable condition",
      "Handle major repairs",
      "Provide 24-hour notice before entry",
    ],

    // Rules and restrictions (defaults)
    petsAllowed: false,
    smokingAllowed: false,
    sublettingAllowed: false,
    maxOccupants: 2,

    // Legal terms
    terminationNotice: 60, // 60 days notice
    renewalTerms:
      "Lease may be renewed upon mutual agreement 60 days before expiration",

    // Status - pending_tenant_signup if tenant not registered, pending_signatures if they are
    status: leaseStatus,

    // Signatures
    landlordSigned: false,
    tenantSigned: false,

    // Timestamps
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  await setDoc(newLeaseRef, {
    ...lease,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Update property or unit status only if tenant exists
  if (tenantExists) {
    if (data.unitId && property.units) {
      // Update specific unit status
      const updatedUnits = property.units.map((unit) =>
        unit.id === data.unitId
          ? {
              ...unit,
              status: "occupied" as const,
              tenantId,
              leaseEnd: data.endDate,
            }
          : unit
      );
      await updateProperty(data.propertyId, { units: updatedUnits });
    } else {
      // Update property status for single-unit
      await updateProperty(data.propertyId, {
        status: "occupied",
        tenantId,
        tenant: data.tenantName,
        leaseEnd: data.endDate,
      });
    }

    // Notify tenant about the lease invitation
    await notifyLeaseCreated(tenantId, newLeaseRef.id, property.title);
  }
  // If tenant doesn't exist, property stays vacant until they sign up

  return newLeaseRef.id;
}

/**
 * Link pending leases to newly signed up tenant
 * Called automatically when a tenant creates an account
 */
export async function linkPendingLeasesToTenant(
  tenantEmail: string,
  tenantId: string
): Promise<number> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  try {
    // Find all pending leases with this email
    const leasesRef = collection(db, "leases");
    const pendingLeasesQuery = query(
      leasesRef,
      where("tenantEmail", "==", tenantEmail),
      where("status", "==", "pending_tenant_signup")
    );

    const pendingLeases = await getDocs(pendingLeasesQuery);

    if (pendingLeases.empty) {
      console.log("No pending leases found for:", tenantEmail);
      return 0;
    }

    console.log(
      `Found ${pendingLeases.size} pending lease(s) for:`,
      tenantEmail
    );

    // Use batch to update all leases atomically
    const batch = writeBatch(db);
    const propertyUpdates: Array<{
      propertyId: string;
      unitId?: string;
      tenantName: string;
      leaseEnd: string;
    }> = [];

    for (const leaseDoc of pendingLeases.docs) {
      const lease = leaseDoc.data();

      // Update lease to link to tenant and change status
      batch.update(leaseDoc.ref, {
        tenantId,
        status: "pending_signatures",
        tenantEmail: null, // Remove temporary email field
        updatedAt: serverTimestamp(),
      });

      // Collect property updates to do after batch
      propertyUpdates.push({
        propertyId: lease.propertyId,
        unitId: lease.unitId,
        tenantName: lease.tenantName || tenantEmail,
        leaseEnd: lease.endDate,
      });

      console.log(`Linked lease ${leaseDoc.id} to tenant ${tenantId}`);
    }

    // Commit all lease updates
    await batch.commit();

    // Update property/unit statuses
    for (const update of propertyUpdates) {
      const property = await getPropertyById(update.propertyId);
      if (!property) continue;

      if (update.unitId && property.units) {
        // Update specific unit
        const updatedUnits = property.units.map((unit) =>
          unit.id === update.unitId
            ? {
                ...unit,
                status: "occupied" as const,
                tenantId,
                leaseEnd: update.leaseEnd,
              }
            : unit
        );
        await updateProperty(update.propertyId, { units: updatedUnits });
      } else {
        // Update single-unit property
        await updateProperty(update.propertyId, {
          status: "occupied",
          tenantId,
          tenant: update.tenantName,
          leaseEnd: update.leaseEnd,
        });
      }
    }

    // Notify tenant about linked leases
    for (const leaseDoc of pendingLeases.docs) {
      const lease = leaseDoc.data();
      const property = await getPropertyById(lease.propertyId);
      if (property) {
        await notifyLeaseCreated(tenantId, leaseDoc.id, property.title);
      }
    }

    console.log(
      `Successfully linked ${pendingLeases.size} lease(s) to tenant ${tenantId}`
    );
    return pendingLeases.size;
  } catch (error) {
    console.error("Error linking pending leases:", error);
    throw error;
  }
}
